import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface UpdateItem {
  field: string;
  currentValue: any;
  proposedValue: any;
  confidence: number;
  reasoning: string;
  approved?: boolean;
}

interface AIAssistantContextType {
  isOpen: boolean;
  businessId: string | null;
  recommendedUpdates?: UpdateItem[];
  onUpdateComplete?: () => void;
  openSidebar: (params: {
    businessId: string;
    recommendedUpdates?: UpdateItem[];
    onUpdateComplete?: () => void;
  }) => void;
  closeSidebar: () => void;
}

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export const useAIAssistant = () => {
  const context = useContext(AIAssistantContext);
  if (context === undefined) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }
  return context;
};

interface AIAssistantProviderProps {
  children: ReactNode;
}

export const AIAssistantProvider = ({ children }: AIAssistantProviderProps) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [recommendedUpdates, setRecommendedUpdates] = useState<UpdateItem[] | undefined>();
  const [onUpdateComplete, setOnUpdateComplete] = useState<(() => void) | undefined>();

  // Automatically close sidebar on settings pages
  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  const openSidebar = (params: {
    businessId: string;
    recommendedUpdates?: UpdateItem[];
    onUpdateComplete?: () => void;
  }) => {
    // Prevent opening sidebar on settings pages
    if (location.pathname.startsWith('/settings')) {
      return;
    }
    
    setBusinessId(params.businessId);
    setRecommendedUpdates(params.recommendedUpdates);
    setOnUpdateComplete(() => params.onUpdateComplete);
    setIsOpen(true);
  };

  const closeSidebar = () => {
    setIsOpen(false);
    setBusinessId(null);
    setRecommendedUpdates(undefined);
    setOnUpdateComplete(undefined);
  };

  const value = {
    isOpen,
    businessId,
    recommendedUpdates,
    onUpdateComplete,
    openSidebar,
    closeSidebar,
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
};